import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import moment from 'moment';
import { FaCalendarAlt } from 'react-icons/fa';
import { Alert, FormGroup, Label } from 'reactstrap';
import Constants from 'config/Constants';
import translate from 'containers/translate';
import DateInput from 'components/dateInput/DateInput';
import { consumesPeriods } from 'contexts/PeriodsContext';
import { consumesPeriodMetadata } from 'contexts/PeriodMetadataContext';
import RemoteDataPropType from 'lib/prop-types/RemoteDataPropType';
import './PeriodEdit.scss';

class PeriodEdit extends Component {
  constructor(props) {
    super(props);

    const initialEndDate = props.selectedPeriod ? props.selectedPeriod.endDate : null;
    const initialEndDateIsEndOfMonth =
      initialEndDate && moment(initialEndDate).isSame(moment(initialEndDate).endOf('month'), 'day');

    this.state = {
      pendingPeriod: props.selectedPeriod,
      endDateMode: initialEndDateIsEndOfMonth ? 'dropdowns' : 'datepicker',
    };

    this.onPeriodDateEdit = this.onPeriodDateEdit.bind(this);
    this.onPeriodEndDateYearEdit = this.onPeriodEndDateYearEdit.bind(this);
    this.onPeriodEndDateMonthEdit = this.onPeriodEndDateMonthEdit.bind(this);
    this.submit = this.submit.bind(this);
  }

  componentDidMount() {
    if (this.props.submitRef) {
      this.props.submitRef.current = this.submit;
    }
  }

  submit(cb) {
    this.props.remotePeriods.api.update(this.state.pendingPeriod, cb, true);
  }

  get activePeriod() {
    return this.props.autoSubmit === false ? this.state.pendingPeriod : this.props.selectedPeriod;
  }

  render() {
    const { t } = this.props;
    const selectedPeriod = this.activePeriod;

    return (
      <div className={'PeriodEdit'}>
        <FormGroup>
          <Label>{t('start_date')}</Label>
          <DateInput
            date={selectedPeriod.startDate}
            locked={this.isStartDateLocked()}
            onInputChange={(date) => this.onPeriodDateEdit(date, 'startDate')}
          />
        </FormGroup>
        <FormGroup>
          <Label>{t('end_date')}</Label>
          {this.renderEndDate()}
        </FormGroup>
        {this.isEndDateLocked() && (
          <Alert color={'info'} className="mt-2">
            {t('period_end_date_not_editable')}
          </Alert>
        )}
        {this.renderVatType()}
      </div>
    );
  }

  renderEndDate() {
    const selectedPeriod = this.activePeriod;

    if (this.state.endDateMode === 'datepicker') {
      return (
        <DateInput
          date={selectedPeriod.endDate}
          locked={this.isEndDateLocked()}
          onInputChange={(date) => this.onPeriodDateEdit(date, 'endDate')}
        />
      );
    }

    const endMonthOptions = this.getEndMonthOptions();
    const endMonth = moment(selectedPeriod.endDate).month().toString();
    const endMonthValue = endMonthOptions.find((option) => option.value === endMonth);
    const endYearOptions = this.getEndYearOptions();
    const endYear = moment(selectedPeriod.endDate).format('YYYY');
    const endYearValue = endYearOptions.find((option) => option.value === endYear);

    return (
      <div className={'endDateSelects'}>
        <Select
          options={endMonthOptions}
          isClearable={false}
          backspaceRemovesValue={false}
          value={endMonthValue}
          onChange={this.onPeriodEndDateMonthEdit}
          isDisabled={this.isEndDateLocked()}
          className="Select"
        />
        <Select
          options={endYearOptions}
          isClearable={false}
          value={endYearValue}
          onChange={this.onPeriodEndDateYearEdit}
          isDisabled={this.isEndDateLocked()}
          className="Select"
        />
        <span className={'endDateModeToggle'} onClick={() => this.setState({ endDateMode: 'datepicker' })}>
          <FaCalendarAlt />
        </span>
      </div>
    );
  }

  getEndMonthOptions() {
    const options = [];

    for (let i = 0; i < 12; i++) {
      options.push({ value: i.toString(), label: moment(i + 1, 'MM').format('MMM') });
    }

    return options;
  }

  getEndYearOptions() {
    const currentEndDate = moment(this.activePeriod.endDate).format('YYYY');
    const currentYear = moment().format('YYYY');
    const numberOfYearsBack = 5;
    const options = [];

    for (let i = 0; i < numberOfYearsBack; i++) {
      options.push({ value: (currentYear - i).toString(), label: (currentYear - i).toString() });
    }

    if (!options.some((o) => o.value === currentEndDate)) {
      options.push({ value: currentEndDate, label: currentEndDate });
    }

    return options;
  }

  isStartDateLocked() {
    return !this.props.periodMetadata.isStartDateEditable;
  }

  isVatTypeLocked() {
    return !this.props.periodMetadata.isVatTypeEditable;
  }

  isEndDateLocked() {
    return !this.props.periodMetadata.isEndDateEditable;
  }

  onPeriodDateEdit(date, property) {
    let period = this.activePeriod;

    if (moment(period[property]).isSame(date, 'day')) {
      return;
    }

    const newPeriod = Object.assign({}, period, { [property]: date });

    if (!moment(newPeriod.endDate).isSameOrBefore(moment(newPeriod.startDate))) {
      period = newPeriod;
    } else if (property === 'startDate') {
      newPeriod.endDate = moment(date).add(3, 'months').endOf('month').format('YYYY-MM-DD');
      period = newPeriod;
    }

    if (this.props.autoSubmit === false) {
      this.setState({ pendingPeriod: period });
    } else {
      this.props.remotePeriods.api.update(period);
    }
  }

  onPeriodEndDateMonthEdit(option) {
    const month = option.value;
    const endDate = moment(this.activePeriod.endDate).month(month).endOf('month');

    this.onPeriodDateEdit(endDate.format('YYYY-MM-DD'), 'endDate');
  }

  onPeriodEndDateYearEdit(option) {
    const year = option.value;
    const endDate = moment(this.activePeriod.endDate).year(year).endOf('month');

    this.onPeriodDateEdit(endDate.format('YYYY-MM-DD'), 'endDate');
  }

  renderVatType() {
    const { t } = this.props;
    const selectedPeriod = this.activePeriod;

    const vatTypes = [
      { value: Constants.VAT_TYPE.SALDO, label: t('proportion') },
      { value: Constants.VAT_TYPE.EFFECTIVE, label: t('effective') },
      { value: Constants.VAT_TYPE.NONE, label: t('none') },
    ];
    const vatType = vatTypes.find((option) => option.value === selectedPeriod.vatType);

    return (
      <>
        <FormGroup>
          <Label for="vatType">{t('vat_type')}</Label>
          <Select
            options={vatTypes}
            isDisabled={this.isVatTypeLocked()}
            isClearable={false}
            backspaceRemovesValue={false}
            value={vatType}
            onChange={(option) => {
              if (this.props.autoSubmit === false) {
                this.setState({ pendingPeriod: { ...selectedPeriod, vatType: option.value } });
              } else {
                this.props.remotePeriods.api.update({ ...selectedPeriod, vatType: option.value });
              }
            }}
            className="Select"
          />
        </FormGroup>
        {this.isVatTypeLocked() && (
          <Alert color={'info'} className="mt-2">
            {t('period_vat_type_change_not_possible')}
          </Alert>
        )}
      </>
    );
  }
}

PeriodEdit.propTypes = {
  selectedPeriod: PropTypes.object,
  remotePeriods: RemoteDataPropType,
  periodMetadata: PropTypes.object.isRequired,
  isStartDateEditable: PropTypes.bool,
  autoSubmit: PropTypes.bool,
  submitRef: PropTypes.shape({ current: PropTypes.any }),
  t: PropTypes.func.isRequired,
};

export default consumesPeriods(consumesPeriodMetadata(translate(PeriodEdit)));
